import { useState } from 'react';
import { plural, t } from '@lingui/core/macro';

import FlatButton from '@rover/components/src/OverflowTextModal/FlatButton';
import { AlertSuccess, ChatInbox, Photo, Rebook, Time } from '@rover/icons';
import type { SvgIcon } from '@rover/types/src/SvgIcon';
import { Box, Button, Flex, Heading, Text } from '@rover/kibble/core';
import IconBlock from '@rover/kibble/patterns/IconBlock/IconBlock';
import type { EnhancedPersonSitterProfile } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfile';
import type { EnhancedPersonSitterProfileAttributes } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfileAttributes';
import type { EnhancedPersonSitterProfileCatAttributes } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfileCatAttributes';
import getBrandSpecificValue from '@rover/shared/js/utils/getBrandSpecificValue';

import type { ViewMode } from '../../hooks/useRevampProfileContext';
import { getIconNameAdapted, renderIcon } from '../../utils/utils';
import ResponsiveOverlay from '../ResponsiveOverlay/ResponsiveOverlay';

const MAX_VISIBLE_ITEMS = 8;

type QualificationItem = {
  key: string;
  icon: SvgIcon | undefined;
  text: string;
};

const buildCommunicationItems = (info: EnhancedPersonSitterProfile): QualificationItem[] => {
  const items: QualificationItem[] = [];
  const repeatCount = info.providerProfile?.repeatClientCount || 0;
  const { percent, timeUsualDesc: responseText, photoRate } = info.responsiveness;

  const usesSvd = getBrandSpecificValue({
    rover: t`Sends updates with Rover Cards`,
    catInAFlat: t`Sends updates with Report Cards`,
  });

  if (repeatCount) {
    items.push({
      key: 'repeat-clients',
      icon: Rebook,
      text: plural(repeatCount, { one: '# repeat pet parent', other: '# repeat pet parents' }),
    });
  }
  if (percent) {
    items.push({
      key: 'response-rate',
      icon: ChatInbox,
      text: t`${percent}% response rate`,
    });
  }
  if (responseText) {
    items.push({
      key: 'response-time',
      icon: Time,
      text: responseText,
    });
  }
  if (photoRate) {
    items.push({
      key: 'photo-rate',
      icon: Photo,
      text: t`${photoRate}% bookings with photo updates`,
    });
  }
  if (info.hasUsedSdv) {
    items.push({
      key: 'sdv',
      icon: AlertSuccess,
      text: usesSvd,
    });
  }

  return items;
};

const buildSkillItems = (
  attributes: EnhancedPersonSitterProfileAttributes | EnhancedPersonSitterProfileCatAttributes
): QualificationItem[] => {
  return Object.keys(attributes)
    .map((attrKey) => {
      const { text, icon, key } = attributes[attrKey];
      if (!text) return null;
      const iconRendered = icon ? renderIcon(getIconNameAdapted(icon)) : undefined;
      return {
        key: key || attrKey,
        icon: iconRendered,
        text,
      };
    })
    .filter((item): item is QualificationItem => item !== null);
};

type Props = {
  info: EnhancedPersonSitterProfile;
  viewMode: ViewMode;
  onReadMore: () => void;
};

const RevampQualifications = ({ info, viewMode, onReadMore }: Props): JSX.Element | null => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const communicationItems = buildCommunicationItems(info);

  let skillItems: QualificationItem[] = [];
  if (viewMode.petType === 'cat-only') {
    skillItems = info.catAttributes ? buildSkillItems(info.catAttributes) : [];
  } else {
    skillItems = buildSkillItems(info.attributes);
    if (viewMode.petType === 'mixed' && info.catAttributes) {
      skillItems = [...skillItems, ...buildSkillItems(info.catAttributes)];
    }
  }

  const allItems = [...communicationItems, ...skillItems];

  if (allItems.length === 0) return null;

  const hasOverflow = allItems.length > MAX_VISIBLE_ITEMS;
  const visibleItems = allItems.slice(0, MAX_VISIBLE_ITEMS);

  const midpoint = Math.ceil(visibleItems.length / 2);
  const leftColumn = visibleItems.slice(0, midpoint);
  const rightColumn = visibleItems.slice(midpoint);

  const handleReadMore = (): void => {
    onReadMore();
    setIsModalOpen(true);
  };

  const renderItem = (item: QualificationItem): JSX.Element =>
    item.icon ? (
      <IconBlock
        iconSize="24px"
        size="200"
        fontWeight="regular"
        icon={item.icon}
        title={item.text}
      />
    ) : (
      <Text size="200" fontWeight="regular">
        {item.text}
      </Text>
    );

  const renderColumn = (items: QualificationItem[]): JSX.Element => (
    <Box flex="1">
      {items.map((item) => (
        <Box py="2x" key={item.key}>
          {renderItem(item)}
        </Box>
      ))}
    </Box>
  );

  return (
    <>
      <Box width="100%">
        <Heading size="200" mb="4x" webProps={{ as: 'h3' }}>
          {t`Qualifications`}
        </Heading>
        <Flex flexDirection={['column', 'column', 'row']} sx={{ gap: ['0px', '0px', '56px'] }}>
          {renderColumn(leftColumn)}
          {rightColumn.length > 0 && renderColumn(rightColumn)}
        </Flex>
        {hasOverflow && (
          <Box pt="4x">
            <FlatButton size="200" onClick={handleReadMore} aria-haspopup="dialog">
              {t`Read more`}
            </FlatButton>
          </Box>
        )}
      </Box>
      <ResponsiveOverlay
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        title={t`Qualifications`}
      >
        <Flex flexDirection="column" gap="4x" width="100%">
          {renderColumn(allItems)}
          <Button fullWidth onPress={() => setIsModalOpen(false)}>
            {t`Close`}
          </Button>
        </Flex>
      </ResponsiveOverlay>
    </>
  );
};

export default RevampQualifications;
